import React, { useEffect, useState } from 'react';
import { Search, Eye } from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

// Fallback rates if the live API is unreachable (USD-based, matches currencyStore)
const FALLBACK_RATES: Record<string, number> = {
  USD: 1, EUR: 0.92, GBP: 0.79, NGN: 1580,
  GHS: 15.5, KES: 129, ZAR: 18.5, CAD: 1.36,
  AUD: 1.53, JPY: 149, AED: 3.67, CHF: 0.90,
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', NGN: '₦', GHS: '₵',
  KES: 'KSh', ZAR: 'R', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', AED: 'د.إ', CHF: 'Fr',
};

const formatAmount = (amount: number, currency: string) => {
  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${symbol}${Number(amount).toFixed(2)}`;
};

const PaymentsAdminPage: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>(FALLBACK_RATES);

  useEffect(() => {
    fetchPayments();
    fetchExchangeRates();
  }, []);

  const fetchPayments = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/admin/payments');
      if (res.data.success) setPayments(res.data.data || []);
    } catch {
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const res = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await res.json();
      if (data.rates) setExchangeRates(data.rates);
    } catch {
      // keep FALLBACK_RATES
    }
  };

  // Payments are stored in whatever currency was actually charged.
  // Convert each to USD before summing so revenue isn't GHS + USD added as if equal.
  const toUsd = (amount: number, currency: string) => {
    if (currency === 'USD') return amount;
    const rate = exchangeRates[currency] || 1;
    return amount / rate;
  };

  const filtered = payments.filter((p) => {
    const matchesSearch =
      p.order_number?.toLowerCase().includes(search.toLowerCase()) ||
      p.provider_reference?.toLowerCase().includes(search.toLowerCase());
    const matchesProvider = providerFilter ? p.provider === providerFilter : true;
    const matchesStatus = statusFilter ? p.status === statusFilter : true;
    return matchesSearch && matchesProvider && matchesStatus;
  });

  const totalRevenueUsd = payments
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + toUsd(Number(p.amount), p.currency), 0);

  const providerColors: Record<string, string> = {
    paystack: 'text-green-600 bg-green-50',
    paypal: 'text-blue-600 bg-blue-50',
    google_pay: 'text-amber-600 bg-amber-50',
  };

  const statusColors: Record<string, string> = {
    pending: 'text-amber-600 bg-amber-50',
    completed: 'text-green-600 bg-green-50',
    failed: 'text-red-500 bg-red-50',
    refunded: 'text-purple-600 bg-purple-50',
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
        <h1 className="font-serif text-3xl text-primary">Payments</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-light p-6">
          <p className="text-xs tracking-widest uppercase text-nude mb-2">Total Revenue</p>
          <p className="font-serif text-2xl text-primary">${totalRevenueUsd.toFixed(2)}</p>
          <p className="text-xs text-nude mt-1">USD equivalent, all currencies converted</p>
        </div>
        <div className="bg-white border border-light p-6">
          <p className="text-xs tracking-widest uppercase text-nude mb-2">Total Payments</p>
          <p className="font-serif text-2xl text-primary">{payments.length}</p>
        </div>
        <div className="bg-white border border-light p-6">
          <p className="text-xs tracking-widest uppercase text-nude mb-2">Completed</p>
          <p className="font-serif text-2xl text-green-600">
            {payments.filter((p) => p.status === 'completed').length}
          </p>
        </div>
        <div className="bg-white border border-light p-6">
          <p className="text-xs tracking-widest uppercase text-nude mb-2">Failed</p>
          <p className="font-serif text-2xl text-red-500">
            {payments.filter((p) => p.status === 'failed').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nude" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="SEARCH BY ORDER OR REFERENCE"
            className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
          />
        </div>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="bg-white border border-light px-4 py-3 text-xs tracking-widest uppercase outline-none focus:border-primary transition-colors cursor-pointer"
        >
          <option value="">All Providers</option>
          <option value="paystack">Paystack</option>
          <option value="paypal">PayPal</option>
          <option value="google_pay">Google Pay</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-light px-4 py-3 text-xs tracking-widest uppercase outline-none focus:border-primary transition-colors cursor-pointer"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedPayment(null)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-white w-full max-w-lg p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl text-primary">Payment Details</h2>
              <button
                onClick={() => setSelectedPayment(null)}
                className="text-nude hover:text-primary transition-colors text-xs tracking-widest uppercase"
              >
                Close
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Customer</p>
                  <p className="font-medium">{selectedPayment.customer_name || '—'}</p>
                  <p className="text-nude">{selectedPayment.customer_email || '—'}</p>
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Provider</p>
                  <p className="font-medium capitalize">
                    {selectedPayment.provider?.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Amount</p>
                  <p className="font-medium">
                    {formatAmount(selectedPayment.amount, selectedPayment.currency)} {selectedPayment.currency}
                  </p>
                  {selectedPayment.currency !== 'USD' && (
                    <p className="text-nude mt-1">
                      ≈ ${toUsd(Number(selectedPayment.amount), selectedPayment.currency).toFixed(2)} USD
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Status</p>
                  <span className={`px-2 py-1 rounded tracking-widest uppercase ${
                    statusColors[selectedPayment.status] || 'text-primary bg-light'
                  }`}>
                    {selectedPayment.status}
                  </span>
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Reference</p>
                  <p className="font-mono">{selectedPayment.provider_reference || '—'}</p>
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Order ID</p>
                  <p className="font-mono truncate">{selectedPayment.order_id}</p>
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Date</p>
                  <p>{new Date(selectedPayment.created_at).toLocaleString()}</p>
                </div>
              </div>
              {selectedPayment.provider_response && (
                <div>
                  <p className="text-nude tracking-widest uppercase mb-2">
                    Provider Response
                  </p>
                  <pre className="bg-light p-3 text-xs overflow-auto max-h-32 rounded">
                    {JSON.stringify(selectedPayment.provider_response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Order', 'Provider', 'Amount', 'Currency', 'Status', 'Reference', 'Date', 'Actions'].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase"
                >
                  No payments found
                </td>
              </tr>
            ) : (
              filtered.map((payment) => (
                <tr
                  key={payment.id}
                  className="border-b border-light hover:bg-light/50 transition-colors"
                >
                  <td className="px-6 py-4 text-xs font-mono">
                    {payment.order_id?.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      providerColors[payment.provider] || 'text-primary bg-light'
                    }`}>
                      {payment.provider?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs font-medium">
                    {formatAmount(payment.amount, payment.currency)}
                    {payment.currency !== 'USD' && (
                      <span className="text-nude ml-1">
                        (${toUsd(Number(payment.amount), payment.currency).toFixed(2)})
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude uppercase">
                    {payment.currency}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      statusColors[payment.status] || 'text-primary bg-light'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs font-mono text-nude">
                    {payment.provider_reference
                      ? payment.provider_reference.slice(0, 12) + '...'
                      : '—'}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedPayment(payment)}
                      className="text-nude hover:text-primary transition-colors"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentsAdminPage;